import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BomPartHelper } from 'bom/helpers';
import { useBomComponentActions } from 'bom/hooks';
import { Form } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { IJobPart } from 'jobs/types';
import { Color, Size } from 'theme/enums';

import { ActionType } from './enums';
import { mapCopyMoveToElementData } from './mappers';
import { copyMoveToElementSchema } from './schema';
import { Table } from './Table';
import { ICopyMoveToElementData } from './types';

interface IProps {
  jobId: number;
  bomParts: IJobPart[];
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
}

export const CopyMoveToElement = ({
  jobId,
  bomParts = [],
  isOpen,
  onClose,
  onSubmitSuccess,
}: IProps) => {
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();

  const { upsertBomComponents } = useBomComponentActions();

  const defaultValues = useMemo(() => {
    return {
      element: null,
      componentData: bomParts.map((bomPart) => ({
        id: bomPart.componentId,
        quantity: null,
        isValid: true,
      })),
    };
  }, [bomParts]);

  const onSubmit =
    (reset: UseFormReset<ICopyMoveToElementData>) =>
    async (data: ICopyMoveToElementData, actionType: ActionType) => {
      const payload = mapCopyMoveToElementData(data, bomParts, actionType);

      if (payload.components.length === 0) {
        return;
      }

      const { success } = await upsertBomComponents(payload);

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);
        clearNotification();

        onSubmitSuccess();
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<ICopyMoveToElementData>) => {
    reset?.();
    clearNotification();
    onClose();
  };

  const getSubmitActions = () => {
    return [
      {
        type: ActionType.COPY,
        label: t('buttonLabelCopyToElement'),
        onSubmit,
        disabled: submitDisabled,
      },
      {
        type: ActionType.MOVE,
        label: t('buttonLabelMoveToElement'),
        color: Color.SUCCESS,
        onSubmit,
        disabled: submitDisabled,
      },
    ];
  };

  const subtitle = useMemo(() => {
    if (bomParts.length === 0) {
      return null;
    }

    return t('subtitleCopyMoveToElement', {
      count: bomParts.length,
      partNumber: BomPartHelper.getDisplayedPartNumber(bomParts[0]),
    });
  }, [bomParts, t]);

  return (
    <FormProvider
      schema={copyMoveToElementSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Form
        title={t('titleCopyMoveToElement')}
        subtitle={subtitle}
        size={Size.LG}
        isOpen={isOpen}
        getSubmitActions={getSubmitActions}
        onClose={handleOnClose}
        notificationPosition={NotificationPosition.DRAWER}
        fieldWrapperSxProps={{ height: '60%' }}
        shouldSubmitOnKeyDown={false}
      >
        <Table jobId={jobId} bomParts={bomParts} setSubmitDisabled={setSubmitDisabled} />
      </Form>
    </FormProvider>
  );
};
